import { collection } from 'firebase/firestore';
import { firestore } from '@api/Firebase/Firebase';
import { DeliveryType, Order, OrderStatus } from '@api/Orders/Order';
import {
    NotificationTemplateOverrides,
    resolveTemplateTexts
} from '@api/NotificationTemplates/NotificationTemplates';

/**
 * NOTIFICACIONES DEL CLIENTE — la campana de la app.
 *
 * Es la réplica, para los 14 mil clientes, de lo que el staff ya tiene en el
 * admin. Y hereda su regla fundacional: **la notificación es el DATO; el push
 * es TRANSPORTE.** El aviso se guarda SIEMPRE aquí y se ve en la campana,
 * llegue o no el push — que es lo que permitió sacar avisos del WhatsApp sin
 * dejar al cliente a ciegas.
 *
 * ⚠️ **PARIDAD: réplica EXACTA en krispis-web y krispis-server**
 * (`api/CustomerNotifications/CustomerNotification.ts`). Los 3 repos escriben en
 * la MISMA colección: el admin cuando el pedido avanza en el tablero, el server
 * cuando lo mueven Stripe o Uber, y la web solo lee. Si las copias se separan,
 * un aviso escrito por uno no se vería —o se vería mal— en la campana.
 *
 * ## Por qué UN documento por cliente y no uno por aviso
 *
 * El modelo obvio (un doc por notificación, como el staff) es correcto para 42
 * personas y ruinoso para 14,427 clientes: **cada documento leído se paga**
 * (regla del CLAUDE.md raíz) y la campana vive en la barra de todas las
 * pantallas. Con un doc por aviso, abrir la app costaría una lectura por aviso
 * pendiente; con un doc por cliente cuesta **exactamente 1**, sin query y sin
 * índice — se lee por id, el mismo patrón que ya usa `pos-sessions` para el
 * espejo del POS.
 *
 * Es también lo que decidió **D11** del plan de notificaciones: personales por
 * destinatario, y el costo que no crece con el número de clientes.
 */

export enum CustomerNotificationType {
    Order = 'order',
    Points = 'points',
    Promo = 'promo',
    System = 'system'
}

export interface CustomerNotification {
    /**
     * Llave de idempotencia **y** de deduplicación dentro del arreglo.
     * `{orderId}_{status}`: un webhook de Stripe o de Uber que se reintenta no
     * puede avisar dos veces lo mismo.
     */
    id: string;
    title: string;
    body?: string;
    type: CustomerNotificationType;
    /** Ruta interna de la web, ej. `/order-details?orderId=…`. */
    link?: string;
    orderId?: string;
    /**
     * epoch en milisegundos — **NO un Timestamp de Firestore**, a propósito.
     * Vive DENTRO de un arreglo, y un Timestamp no viaja igual entre el SDK web
     * (`firebase/firestore`) y el de admin (`firebase-admin`): la comparación de
     * `arrayUnion` es por igualdad profunda y dos representaciones distintas del
     * mismo instante dejarían de deduplicar. Un número es idéntico en los 3 repos.
     */
    createdAt: number;
}

export interface CustomerNotificationsDoc {
    customerId: string;
    items: CustomerNotification[];
    /**
     * Todo lo creado en o antes de esta marca está leído.
     *
     * ⚠️ Es una MARCA, no un campo `read` por elemento, y esa es la diferencia
     * que evita perder avisos: marcar leído escribe **un solo campo**, mientras
     * que mutar el arreglo obligaría a reescribirlo entero y una notificación
     * que llegara en ese instante se perdería. Los avisos se agregan con
     * `arrayUnion`, que es atómico y no necesita leer.
     */
    readUpTo?: number;
    updatedAt?: number;
}

export const CUSTOMER_NOTIFICATIONS_COLLECTION = 'customer-notifications';
export const customerNotificationsRef = collection(firestore, CUSTOMER_NOTIFICATIONS_COLLECTION);

/** Cuántos avisos conserva el documento del cliente. */
export const CUSTOMER_NOTIFICATIONS_MAX_ITEMS = 30;

/**
 * A partir de cuántos se recorta. Hay holgura entre el tope y el disparador a
 * propósito: recortar reescribe el arreglo (la única operación que puede perder
 * un aviso recién llegado), así que se hace lo menos posible.
 */
export const CUSTOMER_NOTIFICATIONS_TRIM_AT = 40;

/** El documento del cliente en `customer-notifications`. Se lee por id. */
export const getCustomerNotificationsDocId = (customerId: string): string => customerId;

/** `{orderId}_{status}` — ver `CustomerNotification.id`. */
export const getOrderNotificationId = (orderId: string, status: OrderStatus): string =>
    `${orderId}_${status}`;

export interface BuildOrderNotificationOptions {
    /**
     * ⚠️ Se recibe como dato en vez de derivarse aquí: la regla de mesa vive en
     * `isTableOrder` (`tableKiosk` presente **y** `type !== KIOSK`) y el
     * CLAUDE.md prohíbe reimplementarla. El llamador del admin la pasa; la web y
     * el server la omiten porque no mueven pedidos de mesa.
     */
    esMesa?: boolean;
    /** Para el "ganaste N puntos" del pedido completado, cuando se conoce. */
    puntosGanados?: number;
    /**
     * Textos personalizados desde el admin (colección `notification-templates`).
     * Ausente = los textos por defecto del catálogo. El emisor los pasa ya
     * leídos (con su caché): este módulo sigue siendo puro.
     */
    overrides?: NotificationTemplateOverrides;
    /** Inyectable para poder probar sin reloj. */
    ahora?: number;
}

/**
 * El aviso que le toca a un pedido al entrar a `status`, o `null` si ese estado
 * no le dice nada al cliente.
 *
 * ⚠️ **El folio NO va en el texto.** Es la misma decisión de Irving que rige los
 * WhatsApp: el folio es dato de operación y el cliente lo ve al abrir el pedido.
 */
export const buildOrderNotification = (
    order: Pick<Order, 'id' | 'status' | 'deliveryType'>,
    status: OrderStatus,
    options: BuildOrderNotificationOptions = {}
): CustomerNotification | null => {
    // Sin id no hay llave de deduplicación ni a dónde llevar al cliente: el
    // aviso no tendría cómo existir. (El tipo `Order` del admin lo declara
    // opcional, así que el guard es real y no defensa de adorno.)
    const orderId = order.id || '';
    if (!orderId) return null;

    const esDelivery = order.deliveryType === DeliveryType.Delivery;
    const base = {
        id: getOrderNotificationId(orderId, status),
        type: CustomerNotificationType.Order,
        link: `/order-details?orderId=${orderId}`,
        orderId,
        createdAt: options.ahora ?? Date.now()
    };

    // Cada estado resuelve a una PLANTILLA del catálogo (`NotificationTemplates`):
    // los textos por defecto viven allá, y el admin puede sobreescribirlos sin
    // deploy vía `options.overrides` (colección `notification-templates`).
    let templateKey: string | null = null;
    let vars: Record<string, string | number> = {};

    switch (status) {
        // El cliente está en la pantalla de pago en este mismo instante:
        // avisarle de algo que está viendo es ruido, no información.
        case OrderStatus.WaitingForPayment:
            return null;
        case OrderStatus.Paid:
            templateKey = 'customer_paid';
            break;
        case OrderStatus.Preparing:
            templateKey = 'customer_preparing';
            break;
        case OrderStatus.Ready:
            templateKey = esDelivery
                ? 'customer_ready_delivery'
                : options.esMesa
                  ? 'customer_ready_mesa'
                  : 'customer_ready_pickup';
            break;
        case OrderStatus.Route:
            templateKey = 'customer_route';
            break;
        case OrderStatus.Completed:
            if (options.puntosGanados && options.puntosGanados > 0) {
                templateKey = 'customer_completed_puntos';
                vars = { puntos: options.puntosGanados };
            } else {
                templateKey = 'customer_completed';
            }
            break;
        case OrderStatus.Cancelled:
            templateKey = 'customer_cancelled';
            break;
        default:
            return null;
    }

    const { title, body } = resolveTemplateTexts(templateKey, vars, options.overrides);
    return { ...base, title, body };
};

/** ¿El arreglo ya trae este aviso? Deduplicación por `id`. */
export const hasNotification = (items: CustomerNotification[] | undefined, id: string): boolean =>
    !!items?.some((item) => item.id === id);

/** Más recientes primero. El arreglo se guarda como venga; el orden es de lectura. */
export const sortByNewest = (items: CustomerNotification[] = []): CustomerNotification[] =>
    [...items].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

export const countUnread = (doc?: Pick<CustomerNotificationsDoc, 'items' | 'readUpTo'>): number => {
    if (!doc?.items?.length) return 0;
    const readUpTo = doc.readUpTo || 0;
    return doc.items.filter((item) => (item.createdAt || 0) > readUpTo).length;
};

export const isUnread = (
    item: CustomerNotification,
    readUpTo?: number
): boolean => (item.createdAt || 0) > (readUpTo || 0);

/** Los `CUSTOMER_NOTIFICATIONS_MAX_ITEMS` más recientes. */
export const trimItems = (items: CustomerNotification[] = []): CustomerNotification[] =>
    sortByNewest(items).slice(0, CUSTOMER_NOTIFICATIONS_MAX_ITEMS);

export const needsTrim = (items: CustomerNotification[] = []): boolean =>
    items.length > CUSTOMER_NOTIFICATIONS_TRIM_AT;
